/**
 * BoostTierCards — 3 cards psicológicos para os tiers de destaque.
 *
 * EXPRESS 15€ · "O Gatilho da Urgência"  → laranja/vermelho, ícone Zap
 * SILVER 45€  · "O Equilíbrio Seguro"    → azul/teal, ícone Shield
 * GOLD 99€    · "A Estrela de Ouro"      → dourado, ícone Crown
 *
 * Props:
 *   - tiers: array vindo de /api/meta (boost_tiers)
 *   - onBuy?: (tier) => void  (opcional; se ausente, cards mostram apenas info)
 *   - buying?: string | null  (tier em curso; disable esse botão)
 *   - variant?: "showcase" | "dashboard"  (variação visual)
 *   - jobTitle?: string  (para mostrar "Destacar «vaga X»" quando aplicável)
 *   - compact?: boolean
 */
import { Zap, Shield, Crown, Check, Sparkles } from "lucide-react";
import { Button } from "../ui/button";

const ICONS = { zap: Zap, shield: Shield, crown: Crown };

const STYLES = {
  express: {
    // Urgência — laranja/vermelho vibrante
    frame: "relative bg-gradient-to-br from-orange-50 via-white to-red-50 border-2 border-orange-300 shadow-[0_8px_24px_-8px_rgba(249,115,22,0.25)]",
    pill: "bg-gradient-to-r from-orange-500 to-red-500 text-white",
    price: "text-orange-600",
    icon: "text-orange-500",
    iconBg: "bg-orange-500/10 ring-2 ring-orange-400/40",
    ribbon: "bg-orange-500 text-white",
    ribbonText: "Urgência",
    button: "bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-[0_6px_20px_-6px_rgba(249,115,22,0.7)] ring-1 ring-orange-400",
    accentText: "text-orange-700",
    dot: "bg-orange-500",
  },
  silver: {
    // Equilíbrio — azul/teal confiança
    frame: "relative bg-gradient-to-br from-sky-50 via-white to-cyan-50 border-2 border-sky-300 shadow-[0_10px_28px_-8px_rgba(14,165,233,0.30)]",
    pill: "bg-gradient-to-r from-sky-500 to-cyan-500 text-white",
    price: "text-sky-700",
    icon: "text-sky-600",
    iconBg: "bg-sky-500/10 ring-2 ring-sky-400/40",
    ribbon: "bg-sky-600 text-white",
    ribbonText: "Mais popular",
    button: "bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white shadow-[0_6px_20px_-6px_rgba(14,165,233,0.7)] ring-1 ring-sky-400",
    accentText: "text-sky-800",
    dot: "bg-sky-500",
  },
  gold: {
    // Prestígio — dourado profundo em fundo escuro
    frame: "relative bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 border-2 border-amber-400 shadow-[0_12px_32px_-8px_rgba(245,158,11,0.55)] text-white",
    pill: "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950",
    price: "text-amber-300",
    icon: "text-amber-300",
    iconBg: "bg-amber-400/15 ring-2 ring-amber-400/40",
    ribbon: "bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-950",
    ribbonText: "★ A Estrela de Ouro",
    button: "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-slate-950 font-black shadow-[0_8px_28px_-6px_rgba(245,158,11,0.85)] ring-2 ring-amber-300",
    accentText: "text-amber-200",
    dot: "bg-amber-400",
  },
};

// Fallback se o backend ainda não devolveu os metadados novos
const FALLBACK = [
  { tier: "express", label: "EXPRESS", amount: 1500, days: 5, icon: "zap", tagline: "Urgência — Anuncia agora e destaca-te já",
    description: "Precisas de contratar rápido? Coloca a tua vaga em destaque durante 5 dias no topo dos resultados.",
    features: ["Selo EXPRESS", "5 dias em destaque", "Alertas a candidatos"] },
  { tier: "silver", label: "SILVER", amount: 4500, days: 10, icon: "shield", tagline: "Equilíbrio — Mais visibilidade, mais candidatos",
    description: "O nosso plano mais popular. 10 dias de destaque na plataforma inteira.",
    features: ["Selo SILVER", "10 dias no topo", "E-mails de alerta", "Reforço em redes sociais"] },
  { tier: "gold", label: "PREMIUM GOLD", amount: 9900, days: 30, icon: "crown", tagline: "Prestígio — 30 dias no topo absoluto",
    description: "A estrela de ouro. 30 dias em prioridade máxima em todos os canais.",
    features: ["Selo GOLD", "30 dias em destaque", "Banner na homepage", "Redes sociais premium"] },
];

export function BoostTierCards({ tiers, onBuy, buying, variant = "showcase", jobTitle, credits, onUseCredit, compact = false }) {
  const list = (tiers && tiers.length ? tiers : FALLBACK).filter(t => STYLES[t.tier]);

  return (
    <div className={`grid gap-6 ${compact ? "sm:grid-cols-3" : "grid-cols-1 lg:grid-cols-3"}`}>
      {list.map((t) => {
        const s = STYLES[t.tier];
        const Icon = ICONS[t.icon] || Zap;
        const isBuying = buying === t.tier;
        const creditCount = credits?.[t.tier] || 0;
        return (
          <div key={t.tier}
               data-testid={`boost-card-${t.tier}`}
               className={`rounded-2xl p-6 sm:p-7 flex flex-col ${s.frame} ${t.tier === "silver" ? "lg:-mt-3 lg:scale-[1.03]" : ""}`}>
            {(t.tier === "silver" || t.tier === "gold") && (
              <div className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.15em] ${s.ribbon}`}>
                {s.ribbonText}
              </div>
            )}

            <div className="flex items-center gap-3 mb-3">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center ${s.iconBg}`}>
                <Icon className={`w-5 h-5 ${s.icon}`} strokeWidth={2.2} />
              </div>
              <div>
                <p className={`font-heading font-black tracking-tighter text-lg ${t.tier === "gold" ? "text-white" : "text-slate-900"}`}>
                  {t.label}
                </p>
                <p className={`text-[11px] uppercase tracking-[0.15em] font-bold ${s.accentText}`}>
                  {t.days} dias em destaque
                </p>
              </div>
            </div>

            <p className={`text-sm ${t.tier === "gold" ? "text-amber-100/80" : "text-slate-600"} leading-snug`}>
              {t.tagline}
            </p>

            <div className="my-5 flex items-baseline gap-1">
              <span className={`font-heading font-black tracking-tighter text-5xl ${s.price}`}>
                {(t.amount / 100).toFixed(0)}
              </span>
              <span className={`text-2xl font-black ${s.price}`}>€</span>
              <span className={`text-xs ml-2 ${t.tier === "gold" ? "text-amber-200/60" : "text-slate-500"}`}>· {t.days} dias</span>
            </div>

            <ul className={`space-y-2 mb-6 text-sm ${t.tier === "gold" ? "text-amber-100/90" : "text-slate-700"}`}>
              {(t.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.tier === "gold" ? "text-amber-300" : "text-emerald-500"}`} strokeWidth={3} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto space-y-2">
              {onBuy && (
                <Button data-testid={`boost-buy-${t.tier}`}
                        onClick={() => onBuy(t.tier)}
                        disabled={isBuying}
                        className={`w-full rounded-full font-bold py-6 ${s.button}`}>
                  {isBuying ? "A redirecionar…" : (variant === "showcase"
                      ? <><Sparkles className="w-4 h-4 mr-2" /> Destacar por {(t.amount/100).toFixed(0)}€</>
                      : <>Comprar {t.label}</>)}
                </Button>
              )}
              {onUseCredit && creditCount > 0 && (
                <Button data-testid={`boost-credit-${t.tier}`}
                        onClick={() => onUseCredit(t.tier)}
                        disabled={buying === t.tier + "credit"}
                        variant="outline"
                        className={`w-full rounded-full border-2 ${t.tier === "gold" ? "border-amber-300 text-amber-300 hover:bg-amber-950/30" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}`}>
                  Usar 1 crédito ({creditCount} disponíveis)
                </Button>
              )}
            </div>

            {jobTitle && (
              <p className={`mt-3 text-[11px] text-center ${t.tier === "gold" ? "text-amber-200/70" : "text-slate-500"}`}>
                Para <b>{jobTitle}</b>
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
